import { useState } from 'react';
import { KeyRound, Send, Smartphone } from 'lucide-react';
import { requestOtp, submitOtp } from '@core/firebase/auth';
import { otpErrorKey } from '@core/auth/otpError';
import { signInAsGuest } from '@core/auth/guest';
import { useLanguage } from '@/contexts/LanguageContext';
import { AuthLayout, authButton, authField } from '@/components/AuthLayout';
import { applyFormat, inputAttrs } from '@core/format/inputs';
import { cn } from '@/lib/cn';

/**
 * Phone, then code. Nothing is decided here: the server resolves the account
 * in the same call that verifies the code, and a failed submitOtp never
 * reaches signInWithCustomToken, so there is no local state to roll back.
 *
 * Redesign 2026-10 (the approved sign-in mock): the form in its own column,
 * the brand panel beside it. A new account and an old one take the same step,
 * so there is no "sign up" anywhere.
 */
export default function SignInPage() {
  const { t, language } = useLanguage();
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [errorKey, setErrorKey] = useState<string | null>(null);

  const run = async (work: () => Promise<void>) => {
    // A second send costs the caller one of their own daily codes. The
    // disabled attribute covers the ordinary case; this covers two clicks
    // landing in the same tick, before React has re-rendered the button.
    if (busy) return;
    setBusy(true);
    setErrorKey(null);
    try {
      await work();
    } catch (e) {
      setErrorKey(otpErrorKey(e));
    } finally {
      setBusy(false);
    }
  };

  /*
   * Not through run(): that maps a failure through otpErrorKey, which speaks
   * the vocabulary of codes and cooldowns. A guest failure is neither.
   */
  const enterAsGuest = async () => {
    if (busy) return;
    setBusy(true);
    setErrorKey(null);
    try {
      await signInAsGuest();
    } catch {
      setErrorKey('auth.guestFailed');
    } finally {
      setBusy(false);
    }
  };

  const send = () => run(async () => {
    await requestOtp(phone);
    setSent(true);
  });

  const verify = () => run(async () => {
    await submitOtp(phone, code);
    // Navigation is the AuthProvider's job: the identity hook fires and the
    // root route moves the user on.
  });

  return (
    <AuthLayout cardTestId="signin-card" slide="recite">
      <header>
        <h1 className="text-[32px] font-semibold tracking-tight text-ink lg:text-[34px]">
          {sent ? t('auth.codeTitle') : t('auth.signInTitle')}
        </h1>
        {sent ? (
          <p className="mt-1.5 text-base text-helper">
            {t('auth.codeSentTo')}{' '}
            <bdi dir="ltr" className="font-medium text-ink">{phone}</bdi>
          </p>
        ) : (
          <p className="mt-1.5 text-base text-helper">{t('auth.signInSubtitle')}</p>
        )}
      </header>

      <div className="mt-8 space-y-3.5">
        {!sent ? (
          <>
            <label className="block space-y-2">
              <span className="block text-[15px] font-medium text-ink">{t('auth.phoneLabel')}</span>
              <span className="relative block">
                <input
                  data-testid="phone-input"
                  value={phone}
                  onChange={(e) => setPhone(applyFormat('phone', e.target.value))}
                  placeholder={t('auth.phonePlaceholder')}
                  {...inputAttrs('phone')}
                  className={cn(authField, 'pr-12')}
                />
                <Smartphone
                  className="pointer-events-none absolute right-4 top-1/2 h-5 w-5 -translate-y-1/2 text-helper"
                  strokeWidth={1.8}
                  aria-hidden="true"
                />
              </span>
            </label>
            <p className="text-sm text-helper">{t('auth.phoneHint')}</p>

            <button type="button" data-testid="send-code" onClick={send} disabled={busy} className={cn(authButton, 'mt-2')}>
              <Send className="h-[18px] w-[18px] rtl:-scale-x-100" strokeWidth={1.8} aria-hidden="true" />
              {t('auth.sendCodeLong')}
            </button>
          </>
        ) : (
          <>
            <label className="block space-y-2">
              <span className="block text-[15px] font-medium text-ink">{t('auth.codeLabel')}</span>
              <span className="relative block">
                <input
                  data-testid="code-input"
                  value={code}
                  onChange={(e) => setCode(applyFormat('otp', e.target.value))}
                  placeholder={t('auth.codePlaceholder')}
                  {...inputAttrs('otp')}
                  className={cn(authField, 'px-12 text-center tracking-[0.4em]')}
                />
                <KeyRound
                  className="pointer-events-none absolute right-4 top-1/2 h-5 w-5 -translate-y-1/2 text-helper"
                  strokeWidth={1.8}
                  aria-hidden="true"
                />
              </span>
            </label>
            <button type="button" data-testid="verify-code" onClick={verify} disabled={busy} className={cn(authButton, 'mt-2')}>
              {t('auth.verify')}
            </button>
            {/* A code can be lost to a slow network or a mistyped number. */}
            <div className="flex items-center justify-between gap-3 text-sm">
              <button
                type="button"
                data-testid="change-number"
                onClick={() => { setSent(false); setCode(''); setErrorKey(null); }}
                disabled={busy}
                className="font-medium text-helper underline-offset-4 hover:text-ink hover:underline disabled:opacity-60"
              >
                {t('auth.changeNumber')}
              </button>
              <button
                type="button"
                data-testid="resend-code"
                onClick={send}
                disabled={busy}
                className="font-semibold text-primary underline-offset-4 hover:underline disabled:opacity-60"
              >
                {t('auth.resend')}
              </button>
            </div>
          </>
        )}

        {errorKey && (
          <p data-testid="signin-error" role="alert" className="rounded-2xl bg-danger/10 px-4 py-3 text-sm text-danger">
            {t(errorKey)}
          </p>
        )}

        <div className="flex items-center gap-3 pt-2 text-sm text-helper" aria-hidden="true">
          <span className="h-px flex-1 bg-hair" />
          {t('auth.or')}
          <span className="h-px flex-1 bg-hair" />
        </div>

        {/*
          * A guest gets the whole app without a phone number. The line under
          * it says what the account is — belonging to this browser — before
          * they start, rather than letting them discover it later.
          */}
        <p className="text-center text-base text-helper">
          {t('auth.guestAsk')}{' '}
          <button
            type="button"
            data-testid="enter-as-guest"
            onClick={() => { void enterAsGuest(); }}
            disabled={busy}
            className="font-semibold text-primary underline-offset-4 hover:underline disabled:opacity-60"
          >
            {t('auth.guestEnter')}
          </button>
        </p>
        <p data-testid="guest-note" className="text-center text-xs leading-relaxed text-helper">{t('auth.guestNote')}</p>
      </div>

      <p className="mt-10 text-center text-sm text-helper">
        {t('auth.termsLead')}{' '}
        <a href="/privacy" className="underline underline-offset-4 hover:text-ink">{t('auth.terms')}</a>
        {/* «و» joins the word after it in Arabic; English needs the space. */}
        {' '}{t('auth.and')}{language === 'ar' ? '' : ' '}
        <a href="/privacy" className="underline underline-offset-4 hover:text-ink">{t('auth.privacy')}</a>
      </p>
    </AuthLayout>
  );
}
